import styled from "@emotion/styled";

import {
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
} from "@/mds";
import { AgentLinkButton } from "@/pages/agent/components/agent-link-button";
import { AgentTaskStateIcon } from "@/pages/agent/components/agent-task-state-icon";
import type { AssistantTaskObservable } from "@/store/assistant";
import { isClosedAssistantTaskStatus } from "@/store/assistant/assistant-task-status";

export const AgentLinkedTaskList = ({
  tasks,
  onOpenTask,
  onToggleTaskComplete,
}: {
  tasks: AssistantTaskObservable[];
  onOpenTask: ({ taskId }: { taskId: string }) => void;
  onToggleTaskComplete: ({ taskId }: { taskId: string }) => void;
}) => {
  if (tasks.length === 0) {
    return (
      <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextS}>
        No linked tasks
      </MDSText>
    );
  }

  return (
    <StyledList aria-label="Linked tasks" role="list">
      {tasks.map(task => {
        const title = task.title || "Untitled task";
        const isClosed = isClosedAssistantTaskStatus({ status: task.status });

        return (
          <StyledRow key={task.id} role="listitem">
            <MDSIconButton
              aria-label={isClosed ? `Reopen ${title}` : `Mark ${title} complete`}
              iconKind={() => <AgentTaskStateIcon status={task.status} />}
              size={MDSIconButtonSize.XSmall}
              variant={MDSIconButtonVariant.Transparent}
              onPress={() => onToggleTaskComplete({ taskId: task.id })}
            />
            <StyledTaskLink truncate onClick={() => onOpenTask({ taskId: task.id })}>
              {title}
            </StyledTaskLink>
          </StyledRow>
        );
      })}
    </StyledList>
  );
};

const StyledList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const StyledRow = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  min-width: 0;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};

  &:hover,
  &:focus-within {
    background: ${({ theme }) => theme.colors.background.list.hover};
  }
`;

const StyledTaskLink = styled(AgentLinkButton)`
  min-width: 0;

  && {
    color: ${({ theme }) => theme.colors.text.secondary};
    text-decoration: none;
  }

  &&:hover:not([data-disabled]),
  &&[data-focus-visible] {
    color: ${({ theme }) => theme.colors.text.primary};
    text-decoration-line: underline;
    text-decoration-style: dotted;
  }
`;
